// The Brand Hub — translations dictionary (EN default, NL)
// All static system UI copy flows through this file.

const en = {
  common: {
    loading: "Loading…",
    save_changes: "Save changes",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete",
    confirm: "Confirm",
    close: "Close",
    edit: "Edit",
    done: "Done",
    search: "Search",
    yes: "Yes",
    no: "No",
    role_owner: "Owner",
    role_manager: "Manager",
    role_creator: "Creator",
    role_viewer: "Viewer",
  },
  sidebar: {
    the_brand_hub: "The Brand Hub",
    company_name: "Company Name",
    section_the_brand: "The Brand",
    section_the_tools: "The Tools",
    add_page: "+ add page",
    add_page_tooltip: "Add page",
    settings: "Settings",
    support: "Support",
    profile_label: "Profile",
    collapse: "Collapse",
    expand: "Expand",
    switch_brand: "Switch brand",
    switch_brand_hint: "Switch brand",
    edit_profile: "Edit profile",
    logout: "Log out",
    tool_agent: "The Brand Assistant",
    tool_writer: "The Brand Writer",
    tool_creator: "The Brand Creator",
    tool_tool: "The Brand Tool",
    badge_soon: "SOON",
    badge_pro: "PRO",
  },
  pages: {
    introduction: "Introduction",
    brand_strategy: "Brand Strategy",
    tone_of_voice: "Tone of Voice",
    logo: "Logo",
    colors: "Colors",
    typography: "Typography",
    art_direction: "Art Direction",
    brand_system: "Brand System",
    applications: "Applications",
  },
  settings: {
    title: "Settings",
    tab_appearance: "Appearance",
    tab_profile: "Profile",
    tab_team: "Team",
    tab_ai: "AI Instructions",
    tab_billing: "Billing",
    tab_requests: "Requests",
    // Appearance
    appearance_language_title: "Language",
    appearance_language_sub: "Change the language",
    appearance_language_question: "What language do you want to use?",
    appearance_language_request_title: "Request a new language",
    appearance_language_request_sub: "Missing a language? Let us know which one you'd like us to add.",
    appearance_language_request_placeholder: "e.g. Français, Deutsch, Español…",
    appearance_language_request_submit: "Send request",
    appearance_avatar_title: "Avatar",
    appearance_avatar_sub: "Set the avatar for this workspace",
    workspace_avatar_choose_another: "Choose another",
    workspace_avatar_drop_prompt: "Drop image to upload or click",
    workspace_avatar_drop_release: "Release to upload",
    workspace_avatar_hint: ".png, .jpg, .webp or .svg",
    workspace_avatar_bad_type: "Only PNG, JPG, WEBP or SVG allowed",
    workspace_avatar_uploaded: "Workspace avatar updated",
    workspace_avatar_upload_failed: "Could not update avatar",
    workspace_avatar_viewer_hint: "Only owners and managers can change the workspace avatar.",
    appearance_colors_title: "Colors",
    appearance_colors_sub: "Change the colors of The Brand Hub",
    appearance_header_bg: "Primary color",
    appearance_text: "Text",
    appearance_accent: "Accent",
    // Profile
    profile_title: "Profile",
    profile_sub: "Manage your personal details",
    profile_photo: "Profile photo",
    profile_photo_cta: "Upload your photo",
    profile_photo_drop: "Drop image to upload",
    profile_photo_hint: ".png or .jpg (less than 5 mb)",
    profile_first_name: "First name",
    profile_last_name: "Last name",
    profile_email: "Email address",
    profile_password_title: "Change password",
    profile_password_sub: "Set a new password for your account",
    profile_current_password: "Current password",
    profile_new_password: "New password",
    profile_confirm_password: "Confirm password",
    show_password: "Show password",
    hide_password: "Hide password",
    // Team
    team_title: "Team",
    team_sub: "Manage members and roles",
    team_invite: "Invite",
    team_invite_email: "Email address",
    team_invite_role: "Role",
    team_empty: "No team members yet",
    team_remove_confirm: "Remove this member?",
    team_transfer_title: "Transfer ownership",
    team_transfer_sub: "Hand ownership of this workspace to a manager. You keep access as viewer afterwards.",
    team_transfer_select_label: "New owner",
    team_transfer_select_placeholder: "Pick a manager",
    team_transfer_no_managers: "There are no managers in this workspace yet. Make someone a manager first.",
    team_transfer_cta: "Send transfer email",
    team_transfer_sending: "Sending…",
    team_transfer_confirm: "Send an email to {name} to accept ownership of {brand}? You will become viewer once they confirm.",
    team_transfer_sent: "Transfer email sent to {email}",
    team_transfer_sent_fail: "Transfer started but the email to {email} could not be sent. The email service is temporarily unavailable — try again in a minute or contact support.",
    team_transfer_failed: "Could not send transfer email",
    invite_pending: "Pending invitation",
    invite_resend: "Resend invitation",
    invite_withdraw: "Withdraw invitation",
    invite_withdraw_confirm: "Withdraw the invitation to {email}? The accept link stops working immediately.",
    invite_withdrawn: "Invitation to {email} withdrawn",
    invite_withdraw_failed: "Could not withdraw invitation",
    danger_title: "Danger zone",
    danger_sub: "Permanently delete this workspace. Your account stays. This cannot be undone.",
    danger_delete_cta: "Delete workspace",
    danger_modal_title: "Delete {brand}",
    danger_modal_body: "This permanently removes the workspace, its pages, files, members and subscription. Your account itself is untouched. This cannot be undone.",
    danger_modal_type_hint: "Type the workspace name to confirm:",
    danger_modal_cancel: "Cancel",
    danger_modal_delete: "Delete",
    danger_modal_deleting: "Deleting…",
    danger_delete_success: "Workspace {brand} deleted",
    danger_delete_failed: "Could not delete workspace",
    // Billing
    billing_title: "Billing",
    billing_sub: "Manage your plan and invoices",
    billing_coming_soon: "Billing is coming soon.",
    billing_plan_label: "Current plan",
    billing_plan_pro: "Pro — Monthly",
    billing_plan_credits: "Credits",
    billing_plan_free: "Free",
    billing_renewal: "Renews on",
    billing_expires: "Expires on",
    billing_cancels_on: "Cancels on",
    billing_credits_label: "Credits balance",
    billing_credits_desc: "Each Writer or Creator run uses one credit.",
    billing_upgrade: "Upgrade to Pro",
    billing_buy_credits: "Buy more credits",
    billing_cancel: "Cancel subscription",
    billing_cancel_confirm: "You will keep Pro access until the end of the current billing period. After that the plan reverts to Free. Continue?",
    billing_canceling: "Canceling…",
    billing_cancel_pending: "Scheduled to cancel at period end",
    billing_canceled_ok: "Subscription canceled",
    billing_restricted: "Billing is restricted to Workspace Owners.",
    billing_free_body: "This workspace is on the Free plan. Upgrade to Pro for unlimited Writer and Creator, or buy credits for pay-as-you-go access.",
    // AI Instructions panel
    ai_title: "AI Instructions",
    ai_sub: "Add supplementary prompts or rules per AI tool.",
    ai_save: "Save changes",
    ai_saving: "Saving…",
    ai_saved: "AI Instructions updated",
    ai_save_failed: "Save failed",
    ai_assistant_title: "The Brand Assistant",
    ai_assistant_sub: "Extra rules the Assistant should follow when answering questions.",
    ai_assistant_placeholder: "e.g. Always use British English. Never mention competitors.",
    ai_writer_title: "The Brand Writer",
    ai_writer_sub: "Supplementary rules on top of your Tone of voice page.",
    ai_writer_placeholder: "e.g. Prefer sentences under 20 words. Never use the word 'synergy'.",
    ai_creator_title: "The Brand Creator",
    ai_creator_sub: "Visual guardrails on top of Brand system, Colors and Typography.",
    ai_creator_placeholder: "e.g. Always include ample white space. Never use stock-photo cliches.",
    ai_restricted_title: "Restricted Access",
    ai_restricted_body: "Only Workspace Managers can edit the AI Instructions. You can request changes or ask for manager access below.",
    ai_send_request_to: "Send request to",
    ai_no_managers: "No managers available",
    ai_send_request: "Send request",
    ai_sending: "Sending…",
    ai_pick_manager_first: "Pick a manager first",
    ai_request_sent: "Request sent to {name}",
    ai_request_failed: "Request failed",
    ai_writer_kb_title: "Knowledge base",
    ai_writer_kb_sub: "Upload PDF, DOCX or TXT. The Writer reads these documents on every run.",
    ai_writer_kb_drop: "Drop a file here, or click to pick one",
    ai_writer_kb_formats: "PDF, DOCX, TXT or Markdown · max 15 MB",
    ai_writer_kb_empty: "No documents yet",
    ai_writer_kb_remove: "Remove document",
    ai_writer_kb_remove_confirm: "Remove \"{name}\" from the knowledge base?",
    ai_writer_kb_removed: "Document removed",
    ai_writer_kb_upload_failed: "Upload failed",
    ai_writer_kb_removed_failed: "Could not remove document",
    ai_writer_kb_uploading: "Uploading…",
    ai_writer_kb_no_text: "No text extracted — file is stored but can't be read by the Writer",
    ai_writer_kb_truncated: "Truncated — only the first ~60k characters are read",
    // Page editor
    editor_add_section: "Add section",
    editor_empty_default: "Add section to start",
    editor_empty_view_hint: "Click \"Edit content\" to fill in this page.",
    editor_strategy_first: "Add {step} to start",
    editor_strategy_next: "Add {step}",
    editor_add_core_value: "Add core value",
    editor_cv_name_placeholder: "Core value name",
    editor_cv_text_placeholder: "Description of this core value…",
    editor_add_characteristic: "Add characteristic",
    editor_char_name_placeholder: "Characteristic name",
    editor_char_text_placeholder: "Description of this characteristic…",
    editor_add_audience: "Add target audience",
    editor_audience_name_placeholder: "Target audience name",
    editor_audience_text_placeholder: "Description of this target audience…",
    editor_strategy_skip: "Skip",
    editor_strategy_back: "Back",
    editor_strategy_more: "Add another section",
    // Requests (Owner-only)
    requests_title: "Pending requests",
    requests_sub: "Role-upgrade and AI-access requests from your team. Approve them to promote members instantly.",
    requests_empty: "No pending requests",
    requests_loading: "Loading…",
    requests_type_role: "Role pending request",
    requests_type_ai: "AI access request",
    requests_sentence_role: "{name} requested to be {role}.",
    requests_sentence_ai: "{name} requested AI access ({role}).",
    requests_wants: "wants",
    requests_badge_role: "Role",
    requests_badge_ai: "AI Access",
    requests_approve: "Approve",
    requests_deny: "Deny",
    requests_approved: "Approved",
    requests_denied: "Denied",
    requests_load_failed: "Could not load requests",
    requests_action_failed: "Action failed",
    // Typography editor + Block drawer (editor)
    editor_block_hero_image: "Hero image",
    editor_block_image: "Image",
    editor_block_text: "Text",
    editor_block_heading: "Title",
    editor_block_title_text: "Title and text",
    editor_block_button: "Button",
    editor_block_two_images: "Two images",
    editor_block_image_grid: "Image grid",
    editor_block_colors: "Colors",
    editor_block_typography: "Typeface",
    editor_block_logo: "Logo",
    editor_block_logo_variant: "Add logo variant",
    editor_block_download: "Download",
    editor_block_divider: "Divider",
    editor_typeface_fallback: "Typeface",
    editor_typeface_description_placeholder: "Why and how do you use this typeface?",
    editor_typeface_description_fallback: "Description of this typeface and when to use it.",
    editor_download_typeface: "Download typeface",
    editor_style_label: "Label",
    editor_style_heading: "Heading",
    editor_style_body: "Body text",
    editor_style_quote: "Quote",
    editor_style_tag: "Tag",
    editor_style_description: "Description",
    editor_style_sample: "Sample text",
    editor_style_line_height: "Line height (%)",
    editor_style_letter_spacing: "Spacing (px)",
    editor_style_case: "Case",
    editor_case_none: "Default",
    editor_style_fallback: "Style",
    editor_sample_fallback: "Sample text",
    editor_line_height_label: "Line height",
    editor_letter_spacing_label: "Spacing",
    editor_fonts_uploaded_one: "Typeface uploaded",
    editor_fonts_uploaded_many: "{n} typefaces uploaded",
    editor_upload_failed: "Upload failed",
    editor_save_failed: "Save failed",
    editor_delete_title: "Delete this page?",
    editor_delete_body: "This will permanently delete \u201c{name}\u201d. This cannot be undone.",
    editor_delete_cancel: "No",
    editor_delete_confirm: "Yes, delete",
    editor_block_remove_title: "Remove",
    leave_workspace_title: "Leave workspace",
    leave_workspace_owner_note: "As the Owner, you cannot leave. Transfer ownership first, or delete the entire workspace in the Billing tab.",
    leave_workspace_notify: "The Owner will be notified.",
    leave_workspace_cta: "Leave this workspace",
    leaving: "Leaving\u2026",
    sign_out_title: "Sign out",
    sign_out_sub: "End your session on this device.",
    sign_out_cta: "Sign out",
    invite_warn_no_mail: "Invited, but the email could not be sent",
    invite_success: "Invited {email}",
    role_updated: "Role updated",
    role_update_failed: "Role update failed",
    remove_member_confirm: "Remove {name} from this workspace?",
    member_delete_failed: "Delete failed",
    invite_resent_success: "Invitation re-sent to {email}",
    resend_mail_failed: "Mail could not be sent",
    resend_failed: "Resend failed",
  },
  profile_page: {
    back: "Back",
    title: "Profile Settings",
    subtitle: "Manage your personal account. These settings apply across all your workspaces.",
    account_title: "Account",
    account_subtitle: "Your name and photo are visible to other members of your workspaces.",
    photo_label: "Profile photo",
    photo_choose_another: "Choose a different photo",
    photo_drop_prompt: "Drag a photo or click to upload",
    photo_drop_release: "Release to upload",
    photo_hint: "PNG or JPG, max 4 MB",
    save_changes: "Save changes",
    change_password_title: "Change password",
    change_password_subtitle: "Use a strong password of at least 8 characters.",
    update_password: "Update password",
    pw_current: "Current password",
    pw_new: "New password",
    pw_confirm: "Confirm new password",
    danger_zone: "Danger zone",
    danger_zone_sub: "Deleting your account is permanent. You will be removed from all workspaces. Workspaces you solely manage will also be deleted.",
    delete_account: "Delete account",
    delete_confirm_body: "You are about to permanently delete your account. This cannot be undone. Enter your password to confirm.",
    pw_delete_label: "Password (leave empty for Google login)",
    cancel: "Cancel",
    confirm_delete: "Yes, delete my account",
    deleting: "Deleting…",
    photo_invalid_type: "PNG or JPG only",
    photo_uploaded: "Photo uploaded",
    photo_upload_failed: "Upload failed",
    saved: "Profile updated",
    save_failed: "Save failed",
    delete_failed: "Deletion failed",
    deleted: "Your account has been deleted",
  },
  user_menu: {
    profile_settings: "Profile Settings",
    log_out: "Log out",
    logged_out: "You are logged out",
    logged_in_as: "Logged in as",
  },
  overview: {
    title: "Your brands",
    subtitle: "Pick a brand to continue, or add a new company to your hub.",
    loading: "Loading\u2026",
    no_tagline: "No tagline",
    new_brand_card: "New brand",
    new_brand_card_sub: "Add another company to your hub.",
    create_title: "New brand",
    name_label: "Name",
    tagline_label: "Tagline",
    create_cta: "Create",
    cancel: "Cancel",
    created: "Brand created",
    create_failed: "Create failed",
  },
  support: {
    title: "Support",
    sub: "Find answers fast or reach your Brand Hub contact.",
    sub_viewer: "Find answers to the most common questions.",
    contact_title: "Your Brand Hub contact",
    contact_sub: "We're here to help with anything inside your workspace.",
    contact_person_label: "Contact person",
    contact_phone_label: "Phone",
    contact_email_label: "Email",
    viewer_cta_title: "Need help?",
    viewer_cta_text: "Visit The Brand Hub website for general contact details and resources.",
    viewer_cta_button: "Go to thebrandhub.nl",
    faq_title: "Frequently asked questions",
    faq_search_placeholder: "Search questions and answers…",
    faq_empty: "No questions have been added yet.",
    faq_no_results: "No results for \"{query}\".",
  },
  accept: {
    title: "Accept ownership",
    checking: "Checking the invitation…",
    description: "Confirm to become the owner of {brand}. The current owner will become viewer.",
    this_workspace: "this workspace",
    accept_button: "Accept ownership",
    accepting: "Accepting…",
    login_required: "Please log in to accept this ownership transfer.",
    success: "You are now the owner",
    failed: "Could not accept the transfer",
    go_to_dashboard: "Go to the workspace",
  },
  auth: {
    login_title: "Welcome back",
    login_sub: "Sign in to The Brand Hub",
    email: "Email",
    password: "Password",
    sign_in: "Sign in",
    sign_in_google: "Continue with Google",
    no_account: "No account?",
    sign_up: "Sign up",
    name: "Name",
    create_account: "Create account",
    have_account: "Have an account?",
    logging_in: "Signing in…",
  },
  toasts: {
    saved: "Saved",
    save_failed: "Save failed",
    deleted: "Deleted",
    delete_failed: "Delete failed",
    upload_failed: "Upload failed",
    load_failed_brands: "Could not load brands",
    load_failed_pages: "Could not load pages",
    brand_created: "Brand created",
    create_failed: "Create failed",
    page_deleted: "Page deleted",
    role_updated: "Role updated",
    role_update_failed: "Could not update role",
    invited: "Invited",
    invite_failed: "Invite failed",
    link_copied: "Link copied",
    support_coming: "Support is coming soon",
    password_updated: "Password updated",
    passwords_mismatch: "Passwords do not match",
    password_too_short: "Password must be at least 8 characters",
    password_current_required: "Enter your current password",
    language_requested: "Request sent to the admin",
    language_request_failed: "Could not send the request",
    language_request_empty: "Enter a language name first",
    photo_uploaded: "Photo uploaded",
    invalid_credentials: "Invalid credentials",
  },
};

const nl = {
  common: {
    loading: "Laden…",
    save_changes: "Wijzigingen opslaan",
    saving: "Opslaan…",
    cancel: "Annuleren",
    delete: "Verwijderen",
    confirm: "Bevestigen",
    close: "Sluiten",
    edit: "Bewerken",
    done: "Klaar",
    search: "Zoeken",
    yes: "Ja",
    no: "Nee",
    role_owner: "Eigenaar",
    role_manager: "Manager",
    role_creator: "Creator",
    role_viewer: "Viewer",
  },
  sidebar: {
    the_brand_hub: "The Brand Hub",
    company_name: "Bedrijfsnaam",
    section_the_brand: "Het merk",
    section_the_tools: "De tools",
    add_page: "+ pagina toevoegen",
    add_page_tooltip: "Pagina toevoegen",
    settings: "Instellingen",
    support: "Support",
    profile_label: "Profiel",
    collapse: "Inklappen",
    expand: "Uitklappen",
    switch_brand: "Wissel bedrijf",
    switch_brand_hint: "Wissel bedrijf",
    edit_profile: "Profiel bewerken",
    logout: "Uitloggen",
    tool_agent: "The Brand Assistent",
    tool_writer: "The Brand Writer",
    tool_creator: "The Brand Creator",
    tool_tool: "The Brand Tool",
    badge_soon: "SOON",
    badge_pro: "PRO",
  },
  pages: {
    introduction: "Introductie",
    brand_strategy: "Merkstrategie",
    tone_of_voice: "Tone of voice",
    logo: "Logo",
    colors: "Kleuren",
    typography: "Typografie",
    art_direction: "Art direction",
    brand_system: "Merksysteem",
    applications: "Concept middelen",
  },
  settings: {
    title: "Instellingen",
    tab_appearance: "Weergave",
    tab_profile: "Profiel",
    tab_team: "Team",
    tab_ai: "AI Instructies",
    tab_billing: "Facturatie",
    tab_requests: "Verzoeken",
    appearance_language_title: "Taal",
    appearance_language_sub: "Verander de taal",
    appearance_language_question: "Welke taal wil je gebruiken?",
    appearance_language_request_title: "Nieuwe taal aanvragen",
    appearance_language_request_sub: "Zit je taal er niet bij? Laat ons weten welke je graag wilt zien.",
    appearance_language_request_placeholder: "bijv. Français, Deutsch, Español…",
    appearance_language_request_submit: "Verzoek versturen",
    appearance_avatar_title: "Avatar",
    appearance_avatar_sub: "Stel de avatar van de Workspace in",
    workspace_avatar_choose_another: "Kies een andere",
    workspace_avatar_drop_prompt: "Sleep een afbeelding hierheen of klik",
    workspace_avatar_drop_release: "Laat los om te uploaden",
    workspace_avatar_hint: ".png, .jpg, .webp of .svg",
    workspace_avatar_bad_type: "Alleen PNG, JPG, WEBP of SVG toegestaan",
    workspace_avatar_uploaded: "Workspace avatar bijgewerkt",
    workspace_avatar_upload_failed: "Avatar kon niet worden bijgewerkt",
    workspace_avatar_viewer_hint: "Alleen owners en managers kunnen de workspace-avatar wijzigen.",
    appearance_colors_title: "Kleuren",
    appearance_colors_sub: "Pas de kleuren van The Brand Hub aan",
    appearance_header_bg: "Primaire kleur",
    appearance_text: "Tekst",
    appearance_accent: "Accent",
    profile_title: "Profiel",
    profile_sub: "Beheer je persoonlijke gegevens",
    profile_photo: "Profielfoto",
    profile_photo_cta: "Upload je foto",
    profile_photo_drop: "Laat je foto hier los",
    profile_photo_hint: ".png of .jpg (minder dan 5 mb)",
    profile_first_name: "Voornaam",
    profile_last_name: "Achternaam",
    profile_email: "E-mailadres",
    profile_password_title: "Wachtwoord wijzigen",
    profile_password_sub: "Stel een nieuw wachtwoord in voor je account",
    profile_current_password: "Huidig wachtwoord",
    profile_new_password: "Nieuw wachtwoord",
    profile_confirm_password: "Bevestig wachtwoord",
    show_password: "Wachtwoord tonen",
    hide_password: "Wachtwoord verbergen",
    team_title: "Team",
    team_sub: "Beheer leden en rollen",
    team_invite: "Uitnodigen",
    team_invite_email: "E-mailadres",
    team_invite_role: "Rol",
    team_empty: "Nog geen teamleden",
    team_remove_confirm: "Lid verwijderen?",
    team_transfer_title: "Eigenaarschap overdragen",
    team_transfer_sub: "Draag het eigenaarschap van deze workspace over aan een manager. Jij blijft daarna toegang houden als viewer.",
    team_transfer_select_label: "Nieuwe eigenaar",
    team_transfer_select_placeholder: "Kies een manager",
    team_transfer_no_managers: "Er zijn nog geen managers in deze workspace. Maak eerst iemand manager.",
    team_transfer_cta: "Verstuur overdrachtsmail",
    team_transfer_sending: "Versturen…",
    team_transfer_confirm: "Een e-mail versturen naar {name} om het eigenaarschap van {brand} over te nemen? Jij wordt viewer zodra zij bevestigen.",
    team_transfer_sent: "Overdrachtsmail verstuurd naar {email}",
    team_transfer_sent_fail: "Overdracht gestart, maar de e-mail naar {email} kon niet worden verstuurd. De e-maildienst is tijdelijk niet beschikbaar — probeer het straks opnieuw of neem contact op met support.",
    team_transfer_failed: "Overdrachtsmail kon niet verstuurd worden",
    invite_pending: "Uitnodiging in afwachting",
    invite_resend: "Opnieuw versturen",
    invite_success: "Uitnodiging verstuurd naar {email}",
    invite_warn_no_mail: "Uitnodiging aangemaakt, maar de e-mail kon niet worden verzonden",
    invite_withdraw: "Uitnodiging intrekken",
    invite_withdraw_confirm: "Uitnodiging aan {email} intrekken? De accepteer-link werkt dan direct niet meer.",
    invite_withdrawn: "Uitnodiging aan {email} ingetrokken",
    invite_withdraw_failed: "Intrekken mislukt",
    danger_title: "Danger zone",
    danger_sub: "Verwijder deze omgeving permanent. Je account blijft bestaan. Deze actie is onomkeerbaar.",
    danger_delete_cta: "Omgeving verwijderen",
    danger_modal_title: "{brand} verwijderen",
    danger_modal_body: "Hiermee verwijder je permanent de omgeving, alle pagina's, bestanden, teamleden en het abonnement. Je persoonlijke account blijft bestaan. Dit kan niet ongedaan worden gemaakt.",
    danger_modal_type_hint: "Typ de naam van de omgeving om te bevestigen:",
    danger_modal_cancel: "Annuleren",
    danger_modal_delete: "Verwijderen",
    danger_modal_deleting: "Bezig…",
    danger_delete_success: "Omgeving {brand} verwijderd",
    danger_delete_failed: "Verwijderen mislukt",
    billing_title: "Facturatie",
    billing_sub: "Beheer je abonnement en facturen",
    billing_coming_soon: "Facturatie komt binnenkort.",
    billing_plan_label: "Huidig plan",
    billing_plan_pro: "Pro — Maandelijks",
    billing_plan_credits: "Credits",
    billing_plan_free: "Free",
    billing_renewal: "Verlengt op",
    billing_expires: "Verloopt op",
    billing_cancels_on: "Stopt op",
    billing_credits_label: "Credits-saldo",
    billing_credits_desc: "Elke Writer- of Creator-run kost één credit.",
    billing_upgrade: "Upgrade naar Pro",
    billing_buy_credits: "Koop extra credits",
    billing_cancel: "Abonnement opzeggen",
    billing_cancel_confirm: "Je behoudt Pro-toegang tot het einde van de huidige periode. Daarna valt het plan terug op Free. Doorgaan?",
    billing_canceling: "Opzeggen…",
    billing_cancel_pending: "Wordt opgezegd aan het einde van deze periode",
    billing_canceled_ok: "Abonnement opgezegd",
    billing_restricted: "Facturatie is alleen zichtbaar voor eigenaren van de workspace.",
    billing_free_body: "Deze workspace zit op het Free-plan. Upgrade naar Pro voor onbeperkt Writer en Creator, of koop credits voor pay-as-you-go toegang.",
    // AI Instructions panel
    ai_title: "AI Instructies",
    ai_sub: "Voeg extra prompts of regels toe per AI-tool.",
    ai_save: "Wijzigingen opslaan",
    ai_saving: "Opslaan…",
    ai_saved: "AI Instructies bijgewerkt",
    ai_save_failed: "Opslaan mislukt",
    ai_assistant_title: "The Brand Assistant",
    ai_assistant_sub: "Extra regels waar de Assistant zich aan houdt bij het beantwoorden van vragen.",
    ai_assistant_placeholder: "bijv. Gebruik altijd Nederlands. Noem nooit concurrenten.",
    ai_writer_title: "The Brand Writer",
    ai_writer_sub: "Aanvullende regels bovenop je Tone of voice-pagina.",
    ai_writer_placeholder: "bijv. Hou zinnen onder 20 woorden. Gebruik nooit het woord 'synergie'.",
    ai_creator_title: "The Brand Creator",
    ai_creator_sub: "Visuele guardrails bovenop Merksysteem, Kleuren en Typografie.",
    ai_creator_placeholder: "bijv. Houd altijd veel witruimte aan. Vermijd stockfoto-clichés.",
    ai_restricted_title: "Beperkte toegang",
    ai_restricted_body: "Alleen Workspace Managers kunnen de AI Instructies aanpassen. Je kunt wijzigingen voorstellen of manager-toegang aanvragen hieronder.",
    ai_send_request_to: "Stuur verzoek naar",
    ai_no_managers: "Geen managers beschikbaar",
    ai_send_request: "Verzoek versturen",
    ai_sending: "Versturen…",
    ai_pick_manager_first: "Kies eerst een manager",
    ai_request_sent: "Verzoek verstuurd naar {name}",
    ai_request_failed: "Verzoek mislukt",
    ai_writer_kb_title: "Kennisbank",
    ai_writer_kb_sub: "Upload PDF, DOCX of TXT. Writer leest deze documenten bij elke generatie.",
    ai_writer_kb_drop: "Sleep een bestand hierheen of klik om te kiezen",
    ai_writer_kb_formats: "PDF, DOCX, TXT of Markdown · max 15 MB",
    ai_writer_kb_empty: "Nog geen documenten",
    ai_writer_kb_remove: "Document verwijderen",
    ai_writer_kb_remove_confirm: "\"{name}\" uit de kennisbank verwijderen?",
    ai_writer_kb_removed: "Document verwijderd",
    ai_writer_kb_upload_failed: "Uploaden mislukt",
    ai_writer_kb_removed_failed: "Verwijderen mislukt",
    ai_writer_kb_uploading: "Uploaden…",
    ai_writer_kb_no_text: "Geen tekst gevonden — het bestand staat opgeslagen, maar Writer kan het niet lezen",
    ai_writer_kb_truncated: "Ingekort — alleen de eerste ~60k tekens worden gelezen",
    // Page editor
    editor_add_section: "Sectie toevoegen",
    editor_empty_default: "Voeg sectie toe om te beginnen",
    editor_empty_view_hint: "Klik op \"Edit content\" om je pagina te vullen.",
    editor_strategy_first: "Voeg {step} toe om te beginnen",
    editor_strategy_next: "Voeg {step} toe",
    editor_add_core_value: "Kernwaarde toevoegen",
    editor_cv_name_placeholder: "Naam kernwaarde",
    editor_cv_text_placeholder: "Beschrijving van deze kernwaarde…",
    editor_add_characteristic: "Karakteristiek toevoegen",
    editor_char_name_placeholder: "Naam karakteristiek",
    editor_char_text_placeholder: "Beschrijving van deze karakteristiek…",
    editor_add_audience: "Doelgroep toevoegen",
    editor_audience_name_placeholder: "Naam doelgroep",
    editor_audience_text_placeholder: "Beschrijving van deze doelgroep…",
    editor_strategy_skip: "Overslaan",
    editor_strategy_back: "Terug",
    editor_strategy_more: "Nog een sectie toevoegen",
    editor_block_heading: "Titel",
    editor_block_title_text: "Titel en tekst",
    editor_block_text: "Tekst",
    editor_block_hero_image: "Hero afbeelding",
    editor_block_image: "Afbeelding",
    editor_block_button: "Knop",
    editor_block_two_images: "Twee afbeeldingen",
    editor_block_image_grid: "Afbeeldingenraster",
    editor_block_colors: "Kleuren",
    editor_block_typography: "Lettertype",
    editor_block_logo: "Logo",
    editor_block_logo_variant: "Extra logo variant",
    editor_block_download: "Download",
    editor_block_divider: "Scheiding",
    requests_title: "Openstaande verzoeken",
    requests_sub: "Verzoeken voor een rolupgrade en AI-toegang van je team. Keur ze goed om leden direct te promoveren.",
    requests_empty: "Geen openstaande verzoeken",
    requests_loading: "Laden…",
    requests_type_role: "Rol-upgrade verzoek",
    requests_type_ai: "AI-toegang verzoek",
    requests_sentence_role: "{name} wil {role} worden.",
    requests_sentence_ai: "{name} vraagt AI-toegang aan ({role}).",
    requests_wants: "wil",
    requests_badge_role: "Rol",
    requests_badge_ai: "AI-toegang",
    requests_approve: "Goedkeuren",
    requests_deny: "Weigeren",
    requests_approved: "Goedgekeurd",
    requests_denied: "Afgewezen",
    requests_load_failed: "Kon verzoeken niet laden",
    requests_action_failed: "Mislukt",
  },
  profile_page: {
    back: "Terug",
    title: "Profielinstellingen",
    subtitle: "Beheer je persoonlijke account. Deze instellingen gelden voor al je workspaces.",
    account_title: "Account",
    account_subtitle: "Je naam en foto zijn zichtbaar voor andere leden van je workspaces.",
    photo_label: "Profielfoto",
    photo_choose_another: "Kies een andere foto",
    photo_drop_prompt: "Sleep een foto of klik om te uploaden",
    photo_drop_release: "Laat los om te uploaden",
    photo_hint: "PNG of JPG, max 4 MB",
    save_changes: "Wijzigingen opslaan",
    change_password_title: "Wachtwoord wijzigen",
    change_password_subtitle: "Gebruik een sterk wachtwoord van minimaal 8 tekens.",
    update_password: "Wachtwoord bijwerken",
    pw_current: "Huidig wachtwoord",
    pw_new: "Nieuw wachtwoord",
    pw_confirm: "Bevestig nieuw wachtwoord",
    danger_zone: "Gevarenzone",
    danger_zone_sub: "Je account verwijderen is permanent. Je wordt verwijderd uit alle workspaces. Workspaces die alleen jij beheert worden ook verwijderd.",
    delete_account: "Account verwijderen",
    delete_confirm_body: "Je staat op het punt je account permanent te verwijderen. Dit kan niet ongedaan gemaakt worden. Vul je wachtwoord in om te bevestigen.",
    pw_delete_label: "Wachtwoord (laat leeg bij Google-login)",
    cancel: "Annuleren",
    confirm_delete: "Ja, verwijder mijn account",
    deleting: "Verwijderen…",
    photo_invalid_type: "Alleen PNG of JPG",
    photo_uploaded: "Foto geüpload",
    photo_upload_failed: "Upload mislukt",
    saved: "Profiel bijgewerkt",
    save_failed: "Opslaan mislukt",
    delete_failed: "Verwijderen mislukt",
    deleted: "Je account is verwijderd",
  },
  user_menu: {
    profile_settings: "Profielinstellingen",
    log_out: "Uitloggen",
    logged_out: "Je bent uitgelogd",
    logged_in_as: "Ingelogd als",
  },
  overview: {
    title: "Jouw merken",
    subtitle: "Kies een merk om verder te gaan, of voeg een nieuw bedrijf toe aan je hub.",
    loading: "Laden\u2026",
    no_tagline: "Geen tagline",
    new_brand_card: "Nieuw merk",
    new_brand_card_sub: "Voeg een ander bedrijf toe aan je hub.",
    create_title: "Nieuw merk",
    name_label: "Naam",
    tagline_label: "Tagline",
    create_cta: "Aanmaken",
    cancel: "Annuleren",
    created: "Merk aangemaakt",
    create_failed: "Aanmaken mislukt",
  },
  support: {
    title: "Support",
    sub: "Vind snel een antwoord of neem contact op met je Brand Hub contactpersoon.",
    sub_viewer: "Vind snel een antwoord op de meestgestelde vragen.",
    contact_title: "Je Brand Hub contactpersoon",
    contact_sub: "Wij helpen graag met alles rondom je workspace.",
    contact_person_label: "Contactpersoon",
    contact_phone_label: "Telefoon",
    contact_email_label: "E-mail",
    viewer_cta_title: "Hulp nodig?",
    viewer_cta_text: "Bekijk de algemene contactgegevens op de website van The Brand Hub.",
    viewer_cta_button: "Ga naar thebrandhub.nl",
    faq_title: "Veelgestelde vragen",
    faq_search_placeholder: "Doorzoek vragen en antwoorden…",
    faq_empty: "Er zijn nog geen vragen toegevoegd.",
    faq_no_results: "Geen resultaten voor \"{query}\".",
  },
  accept: {
    title: "Eigenaarschap aanvaarden",
    checking: "Uitnodiging controleren…",
    description: "Bevestig om de nieuwe eigenaar van {brand} te worden. De huidige eigenaar wordt viewer.",
    this_workspace: "deze workspace",
    accept_button: "Eigenaarschap aanvaarden",
    accepting: "Bezig…",
    login_required: "Log in om deze overdracht te aanvaarden.",
    success: "Je bent nu de eigenaar",
    failed: "Overdracht kon niet aanvaard worden",
    go_to_dashboard: "Naar de workspace",
  },
  auth: {
    login_title: "Welkom terug",
    login_sub: "Log in bij The Brand Hub",
    email: "E-mail",
    password: "Wachtwoord",
    sign_in: "Inloggen",
    sign_in_google: "Doorgaan met Google",
    no_account: "Nog geen account?",
    sign_up: "Registreren",
    name: "Naam",
    create_account: "Account aanmaken",
    have_account: "Al een account?",
    logging_in: "Inloggen…",
  },
  toasts: {
    saved: "Opgeslagen",
    save_failed: "Opslaan mislukt",
    deleted: "Verwijderd",
    delete_failed: "Verwijderen mislukt",
    upload_failed: "Upload mislukt",
    load_failed_brands: "Kon merken niet laden",
    load_failed_pages: "Kon pagina's niet laden",
    brand_created: "Merk aangemaakt",
    create_failed: "Aanmaken mislukt",
    page_deleted: "Pagina verwijderd",
    role_updated: "Rol bijgewerkt",
    role_update_failed: "Rol kon niet worden bijgewerkt",
    invited: "Uitgenodigd",
    invite_failed: "Uitnodigen mislukt",
    link_copied: "Link gekopieerd",
    support_coming: "Support komt eraan",
    password_updated: "Wachtwoord bijgewerkt",
    passwords_mismatch: "Wachtwoorden komen niet overeen",
    password_too_short: "Wachtwoord moet minimaal 8 tekens zijn",
    password_current_required: "Voer je huidige wachtwoord in",
    language_requested: "Verzoek verstuurd naar de admin",
    language_request_failed: "Verzoek kon niet verstuurd worden",
    language_request_empty: "Vul eerst een taal in",
    photo_uploaded: "Foto geüpload",
    invalid_credentials: "Onjuiste inloggegevens",
    auto_translate_failed: "Vertalen mislukt. Probeer opnieuw.",
    auto_translate_success: "Taal toegevoegd en toegepast",
  },
};

export const DICTS = { en, nl };
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "nl", label: "Nederlands" },
];
export const DEFAULT_LANG = "en";
